/**
 * Get unique claim identifier from key and index.
 * key:index is always unique (key is file UUID, index is row number).
 */
export const getClaimId = (claim) => {
  return `${claim.key}:${claim.index}`;
};

export const parseClaimId = (claimId) => {
  const [key, index] = claimId.split(":");
  return { key, index: index !== undefined ? parseInt(index) : undefined };
};

/**
 * Detects simple duplicates based on identical date and claimant name.
 * Returns groups of duplicate claims using claim IDs (key:index).
 */
export const detectSimpleDuplicates = (claims) => {
  const groups = [];
  const seen = new Map();

  claims.forEach((claim) => {
    const date = claim._DATE_OF_LOSS || claim._DATE || "";
    const matchKey = `${date}|${claim._CLAIMANT_NAME}`;
    if (!seen.has(matchKey)) {
      seen.set(matchKey, []);
    }
    const claimId = getClaimId(claim);
    // Ensure no duplicate IDs are added to the same group
    if (!seen.get(matchKey).includes(claimId)) {
      seen.get(matchKey).push(claimId);
    }
  });

  seen.forEach((claimIds, matchKey) => {
    if (claimIds.length > 1) {
      groups.push({
        group_key: `simple_dup_${matchKey}`,
        claim_ids: claimIds,
        match_confidence: 0.95,
        field_differences: [],
        non_conforming_fields: [],
        overall_reasoning: "Identical date of loss and claimant name",
      });
    }
  });

  return groups;
};

/**
 * Detect simple renewal matches based on identical date and claimant name between prior/current arrays.
 * Returns groups with prior_claim_ids and current_claim_ids.
 * Note: claim_id for renewals is already set to the comparison key.
 */
export const detectSimpleRenewalMatches = (
  priorClaims = [],
  currentClaims = []
) => {
  const byKeyPrior = new Map();
  const byKeyCurrent = new Map();

  priorClaims.forEach((c) => {
    const matchKey = `${c.date || ""}|${c.claimant_name || ""}`;
    if (!byKeyPrior.has(matchKey)) byKeyPrior.set(matchKey, []);
    byKeyPrior.get(matchKey).push(String(c.claim_id));
  });

  currentClaims.forEach((c) => {
    const matchKey = `${c.date || ""}|${c.claimant_name || ""}`;
    if (!byKeyCurrent.has(matchKey)) byKeyCurrent.set(matchKey, []);
    byKeyCurrent.get(matchKey).push(String(c.claim_id));
  });

  const groups = [];
  byKeyPrior.forEach((priorIds, matchKey) => {
    const currentIds = byKeyCurrent.get(matchKey);
    if (currentIds && (priorIds.length > 0 || currentIds.length > 0)) {
      groups.push({
        group_key: `simple_match_${matchKey}`,
        prior_claim_ids: priorIds,
        current_claim_ids: currentIds,
        match_confidence: 0.95,
        field_differences: [],
        non_conforming_fields: [],
        overall_reasoning: "Identical date of loss and claimant name",
      });
    }
  });

  return groups;
};

export const filterUnmatchedComparisons = (comparisons) => {
  return comparisons.filter((comparison) => {
    const hasDelta = comparison.delta != null && comparison.delta !== 0;
    const isUnmatched = ["different", "new", "missing"].includes(
      comparison.status
    );
    return hasDelta && isUnmatched;
  });
};

export const buildClaimIdMap = (claims, keyField = "claim_id") => {
  return new Map(claims.map((c) => [String(c[keyField]), c]));
};

// Build field differences for a group of claim display objects
// Compares all claims against the first one (reference claim)
// Returns { field_differences, non_conforming_fields, overall_reasoning }
export const buildDifferencesForDuplicateGroup = (claims) => {
  if (!claims || claims.length < 2) {
    return {
      field_differences: [],
      non_conforming_fields: [],
      overall_reasoning: "",
    };
  }

  const fields = [
    "date",
    "claimant_name",
    "description",
    "coverage",
    "total_loss",
    "open_closed",
  ];

  const referenceClaim = claims[0];
  const differencesMap = new Map();
  const nonConformingSet = new Set();

  // Compare each claim (starting from second) against the reference claim
  claims.slice(1).forEach((claim) => {
    fields.forEach((field) => {
      const refVal = referenceClaim[field] ?? null;
      const curVal = claim[field] ?? null;
      const refStr = String(refVal);
      const curStr = String(curVal);

      if (refStr !== curStr) {
        // Track this field as non-conforming
        nonConformingSet.add(field);

        // Store or update the difference for this field
        if (!differencesMap.has(field)) {
          differencesMap.set(field, {
            field_name: field,
            values: [refVal],
            difference_type: "data_correction",
            confidence: 0.9,
            reasoning: `Values differ for ${field.replace(/_/g, " ")}`,
          });
        }

        // Add this claim's value to the list of different values
        const diff = differencesMap.get(field);
        if (!diff.values.includes(curVal)) {
          diff.values.push(curVal);
        }
      }
    });
  });

  // Convert differences map to array with previous_value/current_value format
  const diffs = Array.from(differencesMap.values()).map((diff) => ({
    field_name: diff.field_name,
    previous_value: diff.values[0],
    current_value:
      diff.values.length === 2
        ? diff.values[1]
        : diff.values.slice(1).join(", "),
    difference_type: diff.difference_type,
    confidence: diff.confidence,
    reasoning:
      diff.values.length === 2
        ? diff.reasoning
        : `Multiple values found for ${diff.field_name.replace(
            /_/g,
            " "
          )}: ${diff.values.slice(1).join(", ")}`,
  }));

  const nonConforming = Array.from(nonConformingSet);

  // Generate overall reasoning based on differences
  let reasoning;
  const claimCount = claims.length;
  if (diffs.length === 0) {
    reasoning = `All ${claimCount} claims are identical - all fields match exactly`;
  } else if (diffs.length === 1) {
    reasoning = `${claimCount} claims with identical date of loss and claimant name, but ${diffs[0].field_name.replace(
      /_/g,
      " "
    )} differs`;
  } else if (diffs.length === 2) {
    const fieldNames = diffs
      .map((d) => d.field_name.replace(/_/g, " "))
      .join(" and ");
    reasoning = `${claimCount} claims with identical date of loss and claimant name, but ${fieldNames} differ`;
  } else {
    const fieldNames = diffs
      .slice(0, -1)
      .map((d) => d.field_name.replace(/_/g, " "))
      .join(", ");
    const lastName = diffs[diffs.length - 1].field_name.replace(/_/g, " ");
    reasoning = `${claimCount} claims with identical date of loss and claimant name, but ${fieldNames}, and ${lastName} differ`;
  }

  return {
    field_differences: diffs,
    non_conforming_fields: nonConforming,
    overall_reasoning: reasoning,
  };
};
